import { EstimateWithId } from '../types/estimation';
import { versionDetector } from '../services/estimationVersionDetector';
import { legacyAdapter } from '../services/legacyEstimationAdapter';
import { calculationService } from '../services/calculationService';
import { DetailedEstimationData } from '../types/estimation-items';

export interface PriceCalculationResult {
  displayPrice: number;
  isRecalculated: boolean;
  originalPrice?: number;
  percentageDifference?: number;
  error?: string;
}

export function getDisplayPrice(estimate: EstimateWithId): PriceCalculationResult {
  if (!estimate) {
    return {
      displayPrice: 0,
      isRecalculated: false,
      error: 'Estimation non valide'
    };
  }

  if (estimate.type === 'quick') {
    console.log('🔍 Processing quick estimate:', {
      hasPropertyType: !!estimate.propertyType,
      hasArea: !!estimate.area,
      hasRenovationLevel: !!estimate.renovationLevel,
      hasPriceRange: !!estimate.priceRange,
      priceRange: estimate.priceRange
    });

    if (estimate.priceRange?.min || estimate.priceRange?.max) {
      const quickPrice = estimate.priceRange.min || estimate.priceRange.max || 0;
      return {
        displayPrice: quickPrice,
        isRecalculated: false
      };
    }

    if (estimate.propertyType && estimate.area && estimate.renovationLevel) {
      try {
        const { calculateQuickEstimate } = require('./quickEstimateCalculator');
        const recalculated = calculateQuickEstimate(estimate);
        console.log('✅ Recalculated quick estimate price range:', recalculated);
        return {
          displayPrice: recalculated.min || 0,
          isRecalculated: true,
          originalPrice: 0
        };
      } catch (error) {
        console.error('❌ Error recalculating quick estimate:', error);
      }
    }

    console.warn('⚠️ Quick estimate missing required data for display');
    return {
      displayPrice: 0,
      isRecalculated: false,
      error: 'Données incomplètes'
    };
  }

  if (!versionDetector.isLegacyFormat(estimate)) {
    const currentPrice = estimate.priceRange?.min || estimate.priceRange?.max || 0;
    return {
      displayPrice: currentPrice,
      isRecalculated: false
    };
  }

  try {
    const originalPrice = estimate.priceRange?.max || estimate.priceRange?.min || 0;

    const adaptedData = legacyAdapter.adaptLegacyEstimation(estimate as any);

    const departmentCode = adaptedData.description?.location?.postcode?.substring(0, 2);
    const result = calculationService.calculateEstimate(adaptedData, departmentCode);

    const recalculatedPrice = result.totalTTC;

    let percentageDifference: number | undefined;
    if (originalPrice > 0) {
      const difference = recalculatedPrice - originalPrice;
      percentageDifference = (difference / originalPrice) * 100;
    }

    return {
      displayPrice: recalculatedPrice,
      isRecalculated: true,
      originalPrice,
      percentageDifference
    };
  } catch (error) {
    console.error('Error recalculating price for estimate:', estimate.id, error);

    const fallbackPrice = estimate.priceRange?.max || estimate.priceRange?.min || 0;
    return {
      displayPrice: fallbackPrice,
      isRecalculated: false,
      error: 'Erreur lors du recalcul',
      originalPrice: fallbackPrice
    };
  }
}

export function formatPrice(price: number): string {
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 0
  }).format(price);
}
